import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import { useAppSelector } from '@/app/store/hooks';
import CustomLoader from '@/components/common/CustomLoader.tsx';
import { isAgentRole } from '@/features/servicePointSelection/utils/servicePointUtils';
import { MainContentRefProvider } from '@/routes/context/MainContentRefProvider.tsx';

import ProtectedRoute from './ProtectedRoute.tsx';

const AuthLayout = React.lazy(() => import('../layouts/AuthLayout.tsx'));
const MainLayout = React.lazy(() => import('../layouts/MainLayout.tsx'));
const DashboardPage = React.lazy(() =>
    import('@/pages/dashboard/DashboardPage.tsx').then((module) => ({ default: module.default })),
);
const LoginPage = React.lazy(() =>
    import('@/pages/login/LoginPage.tsx').then((module) => ({ default: module.default })),
);
const SSOCallback = React.lazy(() =>
    import('@/features/login/sso/ssoCallback.tsx').then((module) => ({
        default: module.default,
    })),
);
const CustomerConsent = React.lazy(() =>
    import('../pages/consent/CustomerConsentPage.tsx').then((module) => ({
        default: module.default,
    })),
);
const ShipmentReceipt = React.lazy(() =>
    import('../pages/shipment/shipmentReceipt.tsx').then((module) => ({ default: module.default })),
);

function SuspenseFallback() {
    return (
        <div
            id="divLoadingWrapper"
            className="tw-bg-white/60 tw-fixed tw-inset-0 tw-z-[99999] tw-flex tw-items-center tw-justify-center"
        >
            <CustomLoader />
        </div>
    );
}

export const AppRoutes = () => {
    const auth = useAppSelector((state) => state.authStore.isAuthenticated);
    const user = useAppSelector((state) => state.authStore.user);
    const selectedServicePointId = useAppSelector(
        (state) => state.servicePointProfile.profile?.srvPntFcId,
    );

    /**
     * Non-admin (agent) users must pick a service point before entering the app.
     * Allow them to remain on the login/selection page until an SP is confirmed.
     *
     * Redirect rules:
     * - not authenticated                              → /login (plain login form)
     * - authenticated + admin                          → /main
     * - authenticated + non-admin + SP selected        → /main
     * - authenticated + non-admin + no SP (in-session) → /login (SP selection screen)
     * - authenticated + non-admin + no SP (on reload)  → /login (plain login form)
     *   └─ Ensured by the beforeunload snapshot guard in stateSnapshotPersistence.ts,
     *      which writes isAuthenticated:false for agent users without an SP so the
     *      hydrateSnapshot action starts the app unauthenticated after a page reload.
     *
     * All redirect-to-/main paths use shouldShowLoginPage so that a non-admin user
     * who still needs SP selection can never reach the dashboard via the root (/),
     * wildcard (*), or direct URL entry — even if in-memory auth is true.
     */
    const isNonAdmin = isAgentRole(user);
    const nonAdminNeedsSpSelection = auth && isNonAdmin && !selectedServicePointId;
    const shouldShowLoginPage = !auth || nonAdminNeedsSpSelection;

    return (
        <Routes>
            {/* Public Customer Consent Route (no auth required) */}
            <Route
                path="/customer-consent"
                element={
                    <Suspense fallback={<SuspenseFallback />}>
                        <CustomerConsent />
                    </Suspense>
                }
            />

            {/* Public Shipment Receipt Route (no auth required) */}
            <Route
                path="/shipment-receipt"
                element={
                    <Suspense fallback={<SuspenseFallback />}>
                        <ShipmentReceipt />
                    </Suspense>
                }
            />

            <Route path="/" element={<Navigate to={shouldShowLoginPage ? '/login' : '/main'} />} />

            {/* Authentication Routes */}
            <Route
                path="/"
                element={
                    <Suspense fallback={<SuspenseFallback />}>
                        <AuthLayout />
                    </Suspense>
                }
            >
                <Route
                    path="/login"
                    element={shouldShowLoginPage ? <LoginPage /> : <Navigate to="/main" replace />}
                />
            </Route>

            {/* Protected Dashboard Routes */}
            <Route
                path="/"
                element={
                    <Suspense fallback={<SuspenseFallback />}>
                        <ProtectedRoute>
                            <MainContentRefProvider>
                                <MainLayout />
                            </MainContentRefProvider>
                        </ProtectedRoute>
                    </Suspense>
                }
            >
                {/*<Route path="/main" element={<Dashboard />} />*/}
                <Route path="/main" element={<DashboardPage />} />
            </Route>

            <Route
                path="/sso/callback/azure"
                element={
                    <Suspense fallback={<SuspenseFallback />}>
                        <SSOCallback />
                    </Suspense>
                }
            />

            {/* Fallback: authenticated users with a valid session go to /main;
                non-admin users still pending SP selection go to /login */}
            <Route
                path="*"
                element={<Navigate to={shouldShowLoginPage ? '/login' : '/main'} replace />}
            />
        </Routes>
    );
};
